import React, { useState, useEffect, useRef } from 'react';
import {
  Printer,
  Tags,
  BarChart3,
  Info,
  Settings,
  Package2,
  PenTool
} from 'lucide-react';
import { PListDto, PListDetailsDto, PListLineDto, PalletSummaryDto, CreateOrderSSCCRequest } from '../../../../types/picking';
import { pickingService } from '../../../../services/pickingService';
import { itemsService } from '../../../../services/itemsService';
import { plistOrderService, PListOrderDto } from '../../../../services/plistOrderService';
import { userProfileService } from '../../../../services/userProfileService';
import { UserProfileDto } from '../../../../types/userProfile';
import { stcBinService } from '../../../../services/stcBinService';
import { dataInspectionService } from '../../../../services/dataInspectionService';
import { mandatoryCountingService } from '../../../../services/mandatoryCountingService';
import { inventoryService } from '../../../../services/inventoryService';
import { getProductImage } from '../../../../constants/productImages';
import { BackButton } from '../../../common/BackButton';
import { GS1Scanner, GS1ScannerRef, GS1ScanData } from './GS1Scanner';
import { PalletManager } from './PalletManager';
import { ActiveLine } from './ActiveLine';
import { PickingLinesList } from './PickingLinesList';
import { ImagePanel } from './ImagePanel';
import { StatusBar } from './StatusBar';
import {
  LocationScanModal,
  LocationOverrideModal,
  ImageModal,
  SplitModal,
  QuantityDistributionModal,
  PalletInfoModal,
  DeliveryInfoModal,
  QuantityConfirmModal,
  QuantityDeviationModal,
  SignatureModal,
  SettingsModal,
  PrintModal,
  PickingPrintModal,
  MandatoryCountingModal,
  ZeroPointCountingModal,
  PackageScanModal,
  SerialNumberModal,
  BatchNumberModal
} from './modals';
import { PickingLine, GS1Data, SplitAllocation } from './types';
import { usePalletManagement } from '../../../../hooks/usePalletManagement';
import { isEmptyPallet, isDatabasePallet } from '../../../../types/picking';
import { useSimpleBarcodeScanner } from '../../../../hooks/useSimpleBarcodeScanner';
import { MobileBarcodeCaptureInput } from '../../../common/scanning';
import { parseGS1Barcode as parseGS1BarcodeFull } from '../../../../utils/gs1Parser';

interface ExtendedPickingLine extends PListLineDto {
  isPartiallyPicked?: boolean;
  isFullyPicked?: boolean;
  image?: string;
  requireLocationScan?: boolean;
  requireItemScan?: boolean;
  palletId?: string; // Now refers to SSCC
  itemDescription?: string;
  location?: string;
}

interface ModernPickingDetailsProps {
  pListId: number;
  onBack: () => void;
}

export const ModernPickingDetails: React.FC<ModernPickingDetailsProps> = ({
  pListId,
  onBack
}) => {
  const [pickingDetails, setPickingDetails] = useState<PListDetailsDto | null>(null);
  const [pickingLines, setPickingLines] = useState<ExtendedPickingLine[]>([]);
  const [currentLineIndex, setCurrentLineIndex] = useState<number | null>(null);
  const [activeLineIndex, setActiveLineIndex] = useState<number | null>(null);
  const [previousCompletedLineNo, setPreviousCompletedLineNo] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Track StartDateTime for each picking line (key: pListLineNo, value: ISO datetime string)
  const [lineStartTimes, setLineStartTimes] = useState<Map<number, string>>(new Map());

  // User profile for barcode scanning settings
  const [userProfile, setUserProfile] = useState<UserProfileDto | null>(null);

  // Refs
  const scannerRef = useRef<GS1ScannerRef>(null);

  // Pallet management - using new hook
  const {
    pallets,
    activePalletSSCC,
    activePallet,
    emptyPallets,
    databasePallets,
    setActivePalletSSCC,
    loadPallets,
    createEmptyPallet,
    deleteEmptyPallet
  } = usePalletManagement(pListId);
  const [showPalletManager, setShowPalletManager] = useState(false);

  // Modals and UI state
  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string>('');
  const [showImagePanel, setShowImagePanel] = useState(false);
  const [autoShowImage, setAutoShowImage] = useState(false); // Default off
  const [enableDragAndDrop, setEnableDragAndDrop] = useState(false); // Default off
  const [enableRapidScanning, setEnableRapidScanning] = useState(false); // Default off
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [showPickingPrintModal, setShowPickingPrintModal] = useState(false);
  const [showLocationScan, setShowLocationScan] = useState(false);
  const [locationScan, setLocationScan] = useState('');
  const [showLocationOverride, setShowLocationOverride] = useState(false);
  const [overrideLocation, setOverrideLocation] = useState('');
  const [tempGS1Quantity, setTempGS1Quantity] = useState<number | null>(null);

  // Store pending GS1 scan data for use after location/quantity validation
  const [pendingGS1Data, setPendingGS1Data] = useState<GS1ScanData | null>(null);

  // Editing states
  const [editingLocation, setEditingLocation] = useState<number | null>(null);
  const [newLocation, setNewLocation] = useState('');
  const [editingQuantity, setEditingQuantity] = useState<number | null>(null);
  const [newQuantity, setNewQuantity] = useState('');

  // Split functionality
  const [showSplitModal, setShowSplitModal] = useState(false);
  const [splitLineNo, setSplitLineNo] = useState<number | null>(null);
  const [splitQuantities, setSplitQuantities] = useState<{[palletId: string]: number}>({});

  // Quantity distribution
  const [showQuantityDistribution, setShowQuantityDistribution] = useState(false);
  const [distributionLineNo, setDistributionLineNo] = useState<number | null>(null);
  const [distributionQuantities, setDistributionQuantities] = useState<{[palletId: string]: number}>({});

  // Pallet info
  const [showPalletInfo, setShowPalletInfo] = useState<string | null>(null);

  // Delivery info modal
  const [showDeliveryInfoModal, setShowDeliveryInfoModal] = useState(false);
  const [pListOrders, setPListOrders] = useState<PListOrderDto[]>([]);

  // Quantity confirmation modal
  const [showQuantityConfirm, setShowQuantityConfirm] = useState(false);
  const [quantityToConfirm, setQuantityToConfirm] = useState<number>(0);
  const [lineToConfirm, setLineToConfirm] = useState<ExtendedPickingLine | null>(null);

  // Package scan modal (for batch picking)
  const [showPackageScan, setShowPackageScan] = useState(false);

  // Quantity deviation modal
  const [showQuantityDeviation, setShowQuantityDeviation] = useState(false);
  const [quantityDeviations, setQuantityDeviations] = useState<Array<{
    pListLineNo: number;
    itemNo: string;
    itemDescription: string;
    qtyOrdered: number;
    qtyPicked: number;
    deviation: number;
  }>>([]);

  // Signature modal
  const [showSignatureModal, setShowSignatureModal] = useState(false);

  // Mandatory counting modal
  const [showMandatoryCounting, setShowMandatoryCounting] = useState(false);
  const [mandatoryCountingData, setMandatoryCountingData] = useState<{
    itemNo: string;
    itemDescription: string;
    binCode: string;
    lastCountedDate?: string | null;
    daysSinceLastCounted?: number | null;
    currentQuantity: number;
  } | null>(null);
  const [isSubmittingCounting, setIsSubmittingCounting] = useState(false);

  // Zero-point counting modal
  const [showZeroPointCounting, setShowZeroPointCounting] = useState(false);
  const [zeroPointCountingData, setZeroPointCountingData] = useState<{
    itemNo: string;
    itemDescription: string;
    binCode: string;
    pickedQuantity: number;
    warehouse: string;
    pListLineNo: number;
  } | null>(null);
  const [isSubmittingZeroPoint, setIsSubmittingZeroPoint] = useState(false);

  // Serial number registration modal
  const [showSerialNumberModal, setShowSerialNumberModal] = useState(false);
  const [serialNumberLine, setSerialNumberLine] = useState<ExtendedPickingLine | null>(null);

  // Batch number registration modal
  const [showBatchNumberModal, setShowBatchNumberModal] = useState(false);
  const [batchNumberLine, setBatchNumberLine] = useState<ExtendedPickingLine | null>(null);

  // Global barcode scanner - works without input focus
  // Automatically disabled when modals are open
  const isAnyModalOpen = showPalletManager || showImageModal || showSettingsModal ||
                         showPrintModal || showLocationScan || showLocationOverride ||
                         showSplitModal || showQuantityDistribution || showDeliveryInfoModal ||
                         showQuantityConfirm || showQuantityDeviation || showSignatureModal ||
                         showMandatoryCounting || showZeroPointCounting || showPackageScan ||
                         showSerialNumberModal || showBatchNumberModal;

  useSimpleBarcodeScanner({
    enabled: !isAnyModalOpen, // Disable when any modal is open
    onScan: (barcode: string) => {
      console.log('🌍 Global scan detected:', barcode);

      // Process the barcode using the same logic as GS1Scanner
      processGlobalBarcode(barcode);
    }
  });

  const processGlobalBarcode = (scanInput: string) => {
    console.log('🔍 Processing global barcode:', scanInput);

    // First, remove trailing scanner separators to check for real GS1 structure
    const withoutTrailingSeparators = scanInput.replace(/[~§]+$/g, '').trim();

    // Check if this looks like a GS1-128 structured barcode
    const hasInternalSeparators = withoutTrailingSeparators.includes('~') ||
                                   withoutTrailingSeparators.includes('§') ||
                                   withoutTrailingSeparators.includes(String.fromCharCode(29));
    const hasParentheses = scanInput.includes('(');
    const hasC1Prefix = scanInput.startsWith(']C1');

    const looksLikeGS1 = hasInternalSeparators || hasParentheses || hasC1Prefix;

    let fullGS1Data = null;
    let hasGS1Data = false;

    if (looksLikeGS1) {
      console.log('🔍 GS1 structure detected, attempting GS1 parse...');
      fullGS1Data = parseGS1BarcodeFull(scanInput);
      hasGS1Data = !!(fullGS1Data.parsedAIs && Object.keys(fullGS1Data.parsedAIs).length > 0);
      console.log('📊 GS1 parse result - hasGS1Data:', hasGS1Data, 'parsedAIs:', fullGS1Data?.parsedAIs);
    } else {
      console.log('📝 No GS1 structure detected, treating as plain barcode');
    }

    if (hasGS1Data && fullGS1Data) {
      console.log('📊 Global scan: Detected GS1-128 barcode');

      const gs1Data: GS1ScanData = {
        sscc: fullGS1Data.sscc,
        gtin: fullGS1Data.gtin,
        itemNo: fullGS1Data.gtin,
        quantity: fullGS1Data.quantity,
        batchNo: fullGS1Data.batchNo,
        expiryDate: fullGS1Data.expiryDate || fullGS1Data.bestBeforeDate || fullGS1Data.useByDate
      };

      console.log('📋 Global scan: Extracted GS1 data:', gs1Data);

      const hasSSCC = !!gs1Data.sscc;
      const hasItem = !!(gs1Data.itemNo || gs1Data.gtin);

      // If no relevant GS1 data, treat as plain barcode
      if (!hasSSCC && !hasItem) {
        console.log('⚠️ GS1 detected but no SSCC/GTIN found - treating as plain barcode');
        const cleanedItemNo = scanInput.replace(/[~§]+$/g, '').trim();
        handleScan({ itemNo: cleanedItemNo });
      }
      // Pre-filled pallet scan (SSCC + Item)
      else if (hasSSCC && hasItem) {
        console.log('📦 Global scan: Pre-filled pallet scan detected (SSCC + Item)');
        handleScan(gs1Data);
      }
      // SSCC only - pallet management
      else if (hasSSCC && !hasItem) {
        console.log('📦 Global scan: SSCC-only scan - pallet management');
        let targetPallet = pallets.find(p => p.sscc === gs1Data.sscc);

        if (!targetPallet) {
          console.log('➕ Creating empty pallet with SSCC:', gs1Data.sscc);
          createEmptyPallet(gs1Data.sscc);
        } else {
          console.log('✅ Activating existing pallet:', targetPallet.sscc);
          setActivePalletSSCC(targetPallet.sscc);
        }
      }
      // Item only
      else if (!hasSSCC && hasItem) {
        console.log('🔎 Global scan: Item-only scan');
        handleScan(gs1Data);
      }
    } else {
      // Plain barcode
      console.log('📝 Global scan: Plain barcode (not GS1-128)');
      const cleanedItemNo = scanInput.replace(/[~§]+$/g, '').trim();
      console.log('🧹 Cleaned plain barcode:', cleanedItemNo);
      handleScan({ itemNo: cleanedItemNo });
    }
  };

  const handleReorder = async (oldIndex: number, newIndex: number) => {
    // Import arrayMove from @dnd-kit/sortable
    const { arrayMove } = await import('@dnd-kit/sortable');

    // Update local state immediately (optimistic update)
    const reorderedLines = arrayMove(pickingLines, oldIndex, newIndex);
    setPickingLines(reorderedLines);

    // Save to backend
    try {
      await pickingService.updateLineOrder(
        pListId,
        reorderedLines.map((line, index) => ({
          pListLineNo: line.pListLineNo,
          sortOrder: index + 1
        }))
      );
    } catch (error) {
      console.error('Error saving line order:', error);
      // Revert on error
      await loadPickingDetails();
    }
  };

  useEffect(() => {
    const loadData = async () => {
      const profile = await loadUserProfile(); // Load user profile FIRST for barcode settings
      await loadPickingDetails(profile); // Then load picking details with the loaded profile
    };
    loadData();
    // loadPallets is now handled by the hook automatically
  }, [pListId]);

  useEffect(() => {
    if (pickingDetails) {
      loadPListOrders(); // Load PListOrders after pickingDetails is loaded
    }
  }, [pickingDetails]);

  // Auto-show image panel when active line changes and has an image (if enabled)
  useEffect(() => {
    // Only process if we have valid data and auto-show is enabled
    if (!autoShowImage || pickingLines.length === 0 || activeLineIndex === null) {
      if (!autoShowImage) {
        setShowImagePanel(false);
      }
      return;
    }

    const currentActiveLine = pickingLines[activeLineIndex];
    if (currentActiveLine?.image) {
      setShowImagePanel(true);
      setCurrentLineIndex(activeLineIndex);
    } else {
      setShowImagePanel(false);
    }
  }, [activeLineIndex, pickingLines, autoShowImage]);

  // Track StartDateTime when active line changes
  useEffect(() => {
    if (activeLineIndex === null || pickingLines.length === 0) {
      return;
    }

    const currentActiveLine = pickingLines[activeLineIndex];
    if (currentActiveLine) {
      const pListLineNo = currentActiveLine.pListLineNo;

      // Set StartDateTime when line becomes active
      setLineStartTimes(prev => {
        const updated = new Map(prev);
        updated.set(pListLineNo, new Date().toISOString());
        return updated;
      });

      console.log(`StartDateTime set for line ${pListLineNo}:`, new Date().toISOString());
    }
  }, [activeLineIndex, pickingLines]);

  // Browser refresh warning - prevent loss of empty pallets
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (emptyPallets.length > 0) {
        e.preventDefault();
        e.returnValue = 'Du har tomme paller som ikke er lagret. Disse vil forsvinne hvis du forlater siden.';
        return e.returnValue;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [emptyPallets]);

  const loadUserProfile = async (): Promise<UserProfileDto | null> => {
    try {
      const profile = await userProfileService.getCurrentUserProfile();
      setUserProfile(profile);
      console.log('User profile loaded:', {
        barCodeConfirmItem: profile.barCodeConfirmItem,
        barCodeConfirmLocation: profile.barCodeConfirmLocation
      });
      return profile;
    } catch (error) {
      console.warn('Could not load user profile, using default barcode settings:', error);
      // If profile fails to load, we'll use safe defaults (require scanning)
      return null;
    }
  };

  const loadPickingDetails = async (profile?: UserProfileDto | null) => {
    try {
      setLoading(true);
      setError(null);
      const details = await pickingService.getPickingDetails(pListId);
      setPickingDetails(details);

      // Fetch item descriptions from Items table
      const itemDescriptions = new Map<string, string>();
      const itemNos = details.lines.map(line => line.itemNo).filter(Boolean) as string[];
      const uniqueItemNos = Array.from(new Set(itemNos));

      // Fetch item details for all unique items
      for (const itemNo of uniqueItemNos) {
        try {
          const item = await itemsService.getItemByCompanyAndItemNo(details.header.companyNo, itemNo!);
          if (item.description1) {
            itemDescriptions.set(itemNo!, item.description1);
          }
        } catch (error) {
          console.warn(`Could not fetch description for item ${itemNo}:`, error);
          // Continue with other items even if one fails
        }
      }

      // Convert to ExtendedPickingLine format with additional properties
      // Use profile settings for barcode requirements, default to false (allow manual picking)
      // Use passed profile parameter first, then fall back to state (for refreshes)
      const effectiveProfile = profile !== undefined ? profile : userProfile;
      const requireLocationScan = effectiveProfile?.barCodeConfirmLocation ?? false;
      const requireItemScan = effectiveProfile?.barCodeConfirmItem ?? false;

      const lines: ExtendedPickingLine[] = details.lines.map((line, index) => ({
        ...line,
        isCompleted: line.qtyPicked > 0,
        isPartiallyPicked: line.qtyPicked > 0 && line.qtyPicked < line.qty,
        isFullyPicked: line.qtyPicked >= line.qty,
        requireLocationScan: requireLocationScan,
        requireItemScan: requireItemScan,
        image: Math.random() > 0.7 ? getProductImage(index) : undefined,
        itemDescription: line.itemNo ? (itemDescriptions.get(line.itemNo) || 'Ingen beskrivelse') : 'Ukjent vare',
        location: line.binCode || 'A-01-01',
        orderNoERP: line.orderNoERP,
        customerName: line.customerName
      }));
      
      // Sort by Sorting field (ascending)
      const sortedLines = lines.sort((a, b) => a.sorting - b.sorting);
      
      // Find first incomplete and check if there are completed lines
      const firstIncompleteIndex = sortedLines.findIndex(line => !line.isCompleted);
      const hasCompletedLines = sortedLines.some(line => line.isCompleted);
      
      // If there are completed lines when loading, set the last one as previous
      if (hasCompletedLines && firstIncompleteIndex !== -1) {
        const completedLines = sortedLines.filter(line => line.isCompleted);
        const lastCompleted = completedLines[completedLines.length - 1];
        setPreviousCompletedLineNo(lastCompleted.pListLineNo);

        // Reorganize with previous
        const reorganized = reorganizeLines(sortedLines, lastCompleted.pListLineNo);
        setPickingLines(reorganized);
        setActiveLineIndex(0); // Active is always at index 0 after reorganize
        // useEffect will handle showing the image panel based on active line
      } else {
        // No completed lines or no incomplete lines - just set sorted
        setPickingLines(sortedLines);
        setActiveLineIndex(firstIncompleteIndex !== -1 ? firstIncompleteIndex : null);
        // useEffect will handle showing the image panel based on active line
      }
    } catch (err) {
      console.error('Error loading picking details:', err);
      setError('Kunne ikke laste plukk-detaljer. Prøv igjen.');
    } finally {
      setLoading(false);
    }
  };

  const loadPListOrders = async () => {
    try {
      const companyNo = pickingDetails!.header.companyNo;
      console.log('Loading PListOrders for PListID:', pListId, 'CompanyNo:', companyNo);
      const orders = await plistOrderService.getByPListId(companyNo, pListId);
      console.log('Loaded PListOrders:', orders);
      setPListOrders(orders);
    } catch (error) {
      console.error('Error loading PListOrders:', error);
      // Don't show error as PListOrders might not exist
    }
  };

  const activeLine = activeLineIndex !== null ? pickingLines[activeLineIndex] : null;
  // activePallet is now from the hook
  const currentLine = currentLineIndex !== null ? pickingLines[currentLineIndex] : null;
  const completedLines = pickingLines.filter(line => line.isCompleted).length;

  // Convert UnifiedPallet[] to PalletSummaryDto[] for components
  const palletSummaries: PalletSummaryDto[] = pallets.map(p => {
    if (isEmptyPallet(p)) {
      // Convert empty pallet to PalletSummaryDto format
      return {
        sscc: p.sscc,
        ssccLevel: p.ssccLevel,
        totalItems: 0,
        totalQuantity: 0,
        totalWeight: 0,
        isActive: p.sscc === activePalletSSCC,
        created: p.created,
        createdBy: p.createdBy,
        items: []
      };
    } else {
      // Database pallet - just add isActive field
      return {
        ...p,
        isActive: p.sscc === activePalletSSCC
      };
    }
  });

  // Convert activePallet to PalletSummaryDto format
  const activePalletSummary: PalletSummaryDto | undefined = activePallet
    ? palletSummaries.find(p => p.sscc === activePallet.sscc)
    : undefined;

  // Reorganize lines: Aktiv (1), Forrige (2), Neste (3), Andre ufullførte (4), Andre fullførte (5)
  const reorganizeLines = (lines: ExtendedPickingLine[], prevCompletedLineNo: number | null): ExtendedPickingLine[] => {
    // Sorter alle linjer etter Sorting først
    const sortedLines = [...lines].sort((a, b) => a.sorting - b.sorting);
    
    const completed = sortedLines.filter(line => line.isCompleted);
    const uncompleted = sortedLines.filter(line => !line.isCompleted);
    
    if (uncompleted.length === 0) {
      // Ingen ufullførte linjer - bare vis alle fullførte
      return completed;
    }
    
    const activeLine = uncompleted[0]; // Første ufullførte er aktiv
    const previousLine = prevCompletedLineNo !== null 
      ? completed.find(line => line.pListLineNo === prevCompletedLineNo) 
      : null;
    const nextLine = uncompleted.length > 1 ? uncompleted[1] : null;
    
    // Bygg rekkefølge:
    const result: ExtendedPickingLine[] = [];
    
    // 1. Aktiv linje (topp)
    result.push(activeLine);
    
    // 2. Forrige fullførte linje (gul)
    if (previousLine) {
      result.push(previousLine);
    }
    
    // 3. Neste linje etter aktiv (hvit)
    if (nextLine) {
      result.push(nextLine);
    }
    
    // 4. Andre ufullførte linjer (unntatt aktiv og neste)
    const otherUncompleted = uncompleted.slice(2);
    result.push(...otherUncompleted);
    
    // 5. Andre fullførte linjer (unntatt forrige) - grønne nederst
    const otherCompleted = completed.filter(line => line.pListLineNo !== prevCompletedLineNo);
    result.push(...otherCompleted);
    
    return result;
  };

  // Handle line selection from list
  const handleLineSelect = (index: number) => {
    setActiveLineIndex(index);
    setCurrentLineIndex(index);

    const line = pickingLines[index];
    if (line?.image && autoShowImage) {
      setShowImagePanel(true);
    }
  };

  // Handle manual image show/hide button click (toggle)
  const handleShowImage = () => {
    if (activeLine?.image) {
      if (showImagePanel) {
        // If image is already showing, close it
        setShowImagePanel(false);
      } else {
        // If image is not showing, open it
        setShowImagePanel(true);
        setCurrentLineIndex(activeLineIndex);
      }
    }
  };

  /**
   * Helper function to show quantity confirmation with package scan check.
   * For batch picks with packageNo, shows PackageScanModal first.
   * Otherwise shows QuantityConfirmModal directly.
   */
  const showQuantityConfirmWithPackageCheck = (line: ExtendedPickingLine, quantity: number) => {
    setLineToConfirm(line);
    setQuantityToConfirm(quantity);

    // Check if this is a batch pick AND line has a package assigned
    const isBatchPick = pickingDetails?.header.pListIsBatchYesNo === true;
    const hasPackage = line.packageNo != null && line.packageNo > 0;

    if (isBatchPick && hasPackage) {
      // Show package scan modal first
      setShowPackageScan(true);
    } else {
      // Show quantity confirm directly
      setShowQuantityConfirm(true);
    }
  };

  /**
   * Handler when package scan is confirmed - proceed to quantity confirmation
   */
  const handlePackageScanConfirm = () => {
    setShowPackageScan(false);
    setShowQuantityConfirm(true);
  };

  // Parse GS1 128 barcode
  const parseGS1Barcode = (barcode: string): GS1Data => {
    const gs1Data: GS1Data = {};
    
    const patterns = {
      sscc: /\(00\)(\d{18})/,
      itemNo: /\(01\)([^()]+?)(?=\(|$)/,
      quantity: /\(30\)(\d+)/,
      batch: /\(10\)([^()]+?)(?=\(|$)/,
      expiry: /\(17\)(\d{6})/
    };

    Object.entries(patterns).forEach(([key, pattern]) => {
      const match = barcode.match(pattern);
      if (match) {
        if (key === 'quantity') {
          gs1Data[key] = parseInt(match[1]);
        } else if (key === 'itemNo') {
          const gtin = match[1];
          const varMatch = gtin.match(/VAR\d+/);
          if (varMatch) {
            gs1Data[key] = varMatch[0];
          } else {
            const trimmed = gtin.replace(/^0+/, '') || gtin.slice(-6);
            gs1Data[key] = trimmed;
          }
        } else {
          gs1Data[key as keyof GS1Data] = match[1] as any;
        }
      }
    });

    return gs1Data;
  };

  // Find matching line by scanned item
  const findLineByItem = (itemNo: string): ExtendedPickingLine | null => {
    const itemNoLower = itemNo.toLowerCase();

    // Also try matching with leading zeros removed (for GTIN matching)
    const cleanedItemNo = itemNo.replace(/^0+/, '') || itemNo;
    const cleanedItemNoLower = cleanedItemNo.toLowerCase();

    // Helper function to check if scanned item matches line
    const matches = (line: ExtendedPickingLine): boolean => {
      const lineItemNo = line.itemNo?.toLowerCase();
      const lineBarCode = line.itemBarCode?.toLowerCase();

      // Match against itemNo (with and without leading zeros)
      if (lineItemNo && (lineItemNo === itemNoLower || lineItemNo === cleanedItemNoLower)) {
        return true;
      }

      // Match against itemBarCode (with and without leading zeros)
      if (lineBarCode && (lineBarCode === itemNoLower || lineBarCode === cleanedItemNoLower)) {
        return true;
      }

      return false;
    };

    // First try to find incomplete line (prefer incomplete over completed)
    const incompleteLine = pickingLines.find(line => matches(line) && !line.isCompleted);

    if (incompleteLine) {
      return incompleteLine;
    }

    // Then try any line (including completed)
    return pickingLines.find(line => matches(line)) || null;
  };

  const handleScan = async (scanData: GS1ScanData) => {
    console.log('🔍 ModernPickingDetails: Processing scan with full GS1 data', scanData);

    const { itemNo, quantity, sscc, batchNo, expiryDate, gtin } = scanData;

    console.log('🔍 ModernPickingDetails: Destructured - itemNo=', itemNo, 'gtin=', gtin, 'sscc=', sscc);
    console.log('🔍 ModernPickingDetails: effectiveItemNo will be:', itemNo || gtin);

    // SCENARIO 1: SSCC-only scan (no item) - handled in GS1Scanner, shouldn't reach here
    if (sscc && !itemNo && !gtin) {
      console.log('⚠️ SSCC-only scan should be handled in GS1Scanner');
      return;
    }

    // SCENARIO 2: Pre-filled pallet scan (SSCC + Item in same barcode)
    // Store SSCC info and continue to location/quantity validation
    // The pallet will be created when user confirms quantity
    if (sscc && (itemNo || gtin)) {
      console.log('📦 Pre-filled pallet detected - storing SSCC for later pallet creation');
      // Store the full GS1 data for use after quantity confirmation
      setPendingGS1Data(scanData);

      // Check if pallet already exists
      let targetPallet = pallets.find(p => p.sscc === sscc);
      if (targetPallet) {
        console.log('✅ Pallet already exists, setting as active');
        setActivePalletSSCC(sscc);
      }
      // Continue to location/quantity validation below
      // The SSCC info in scanData will be used when quantity is confirmed
    }

    // Use GTIN if itemNo is not present
    const effectiveItemNo = itemNo || gtin;
    if (!effectiveItemNo) {
      console.log('⚠️ No item identifier found in scan');
      return;
    }

    // Helper function to check if scanned item matches line
    const itemsMatch = (scannedItem: string, line: ExtendedPickingLine): boolean => {
      const scannedLower = scannedItem.toLowerCase();
      const cleanedScanned = scannedItem.replace(/^0+/, '') || scannedItem;
      const cleanedScannedLower = cleanedScanned.toLowerCase();

      console.log(`🔎 Matching: scanned="${scannedItem}" (cleaned="${cleanedScanned}") against line.itemNo="${line.itemNo}", line.itemBarCode="${line.itemBarCode}"`);

      // Match against itemNo
      if (line.itemNo) {
        const itemNoLower = line.itemNo.toLowerCase();
        if (itemNoLower === scannedLower || itemNoLower === cleanedScannedLower) {
          console.log(`✅ MATCH on itemNo: ${line.itemNo}`);
          return true;
        }
      }

      // Match against itemBarCode
      if (line.itemBarCode) {
        const barCodeLower = line.itemBarCode.toLowerCase();
        if (barCodeLower === scannedLower || barCodeLower === cleanedScannedLower) {
          console.log(`✅ MATCH on itemBarCode: ${line.itemBarCode}`);
          return true;
        }
      }

      console.log(`❌ NO MATCH for this line`);
      return false;
    };

    // RAPID SCANNING MODE: Skip all dialogs and directly update quantity
    if (enableRapidScanning) {
      console.log('⚡ Rapid scanning mode enabled');

      // Find matching line (prefer incomplete lines)
      const matchedLine = findLineByItem(effectiveItemNo);

      if (!matchedLine) {
        // Show error but don't block - just focus back on scanner
        console.error('❌ Item not found in picking list:', effectiveItemNo);
        alert('Vare ikke funnet i plukklisten!');
        return;
      }

      // Set matched line as active
      const lineIndex = pickingLines.indexOf(matchedLine);
      setCurrentLineIndex(lineIndex);
      setActiveLineIndex(lineIndex);

      // Calculate new quantity (increment by 1)
      const newTotalQuantity = matchedLine.qtyPicked + 1;

      // Validate that we don't exceed ordered quantity
      if (newTotalQuantity > matchedLine.qty) {
        alert(`Maksimalt antall nådd (${matchedLine.qty})`);
        return;
      }

      // Store pending GS1 data for pallet creation
      if (sscc && (itemNo || gtin)) {
        setPendingGS1Data(scanData);

        // Create/activate pallet if needed
        let targetPallet = pallets.find(p => p.sscc === sscc);
        if (!targetPallet) {
          await createNewPallet(sscc, scanData);
        } else {
          setActivePalletSSCC(sscc);
        }
      }

      // Directly update quantity without any dialogs
      try {
        await handleUpdateQuantity(matchedLine.pListLineNo, newTotalQuantity);
        console.log('✅ Rapid scan completed successfully');
      } catch (error) {
        console.error('Error in rapid scan update:', error);
        alert('Kunne ikke oppdatere antall. Prøv igjen.');
      }

      return;
    }

    // NORMAL MODE: Original logic with dialogs
    // Check if scanned item matches active line (case-insensitive with GTIN cleaning)
    if (activeLine && itemsMatch(effectiveItemNo, activeLine)) {
      const activeIndex = activeLineIndex!;
      setCurrentLineIndex(activeIndex); // Set visual selection

      if (activeLine.image && autoShowImage) {
        setShowImagePanel(true);
      }

      const quantityToAdd = quantity || (activeLine.qty - activeLine.qtyPicked);

      // TODO: If we have batch/expiry from GS1, we should create OrderSSCC record here too
      console.log('Scanned item data:', { batchNo, expiryDate, gtin });

      if (activeLine.requireLocationScan) {
        setShowLocationScan(true);
        setTempGS1Quantity(quantityToAdd);
      } else {
        // Show quantity confirmation modal (with package scan check for batch picks)
        showQuantityConfirmWithPackageCheck(activeLine, quantityToAdd);
      }
    } else {
      // Fallback to original logic for non-active line scanning
      const matchedLine = findLineByItem(effectiveItemNo);

      if (matchedLine) {
        const lineIndex = pickingLines.indexOf(matchedLine);
        setCurrentLineIndex(lineIndex); // Set visual selection
        setActiveLineIndex(lineIndex); // Override active line

        if (matchedLine.image && autoShowImage) {
          setShowImagePanel(true);
        }

        const quantityToAdd = quantity || (matchedLine.qty - matchedLine.qtyPicked);

        if (matchedLine.requireLocationScan) {
          setShowLocationScan(true);
          setTempGS1Quantity(quantityToAdd);
        } else {
          // Show quantity confirmation modal (with package scan check for batch picks)
          showQuantityConfirmWithPackageCheck(matchedLine, quantityToAdd);
        }
      } else {
        alert('Vare ikke funnet i plukklisten!');
      }
    }
  };

  const handleMandatoryCountingSubmit = async (countedQuantity: number) => {
    if (!currentLine || !pickingDetails || !mandatoryCountingData) return;

    setIsSubmittingCounting(true);
    try {
      const companyNo = pickingDetails.header.companyNo;
      await mandatoryCountingService.submitMandatoryCounting(companyNo, {
        warehouseID: currentLine.warehouseID,
        itemNo: mandatoryCountingData.itemNo,
        binCode: mandatoryCountingData.binCode,
        countedQuantity: countedQuantity,
        pListID: pListId,
        pListLineNo: currentLine.pListLineNo
      });

      // Close mandatory counting modal
      setShowMandatoryCounting(false);
      setMandatoryCountingData(null);

      // Continue with normal picking flow (quantity confirmation with package scan check)
      const quantityToAdd = tempGS1Quantity !== null ? tempGS1Quantity : (currentLine.qty - currentLine.qtyPicked);
      showQuantityConfirmWithPackageCheck(currentLine, quantityToAdd);
      setTempGS1Quantity(null);
    } catch (error) {
      console.error('Error submitting mandatory counting:', error);
      alert('Kunne ikke registrere telling. Prøv igjen.');
    } finally {
      setIsSubmittingCounting(false);
    }
  };

  const handleLocationScanConfirm = async () => {
    if (!currentLine || !pickingDetails) return;

    if (locationScan.toLowerCase() === currentLine.location?.toLowerCase() || overrideLocation) {
      // Check for mandatory counting requirement
      try {
        const companyNo = pickingDetails.header.companyNo;
        const checkResult = await mandatoryCountingService.checkMandatoryCounting(companyNo, {
          warehouseId: currentLine.warehouseID,
          itemNo: currentLine.itemNo || '',
          binCode: currentLine.location || ''
        });

        if (checkResult.requiresCounting) {
          // Block picking flow and show mandatory counting modal
          setShowLocationScan(false);
          setLocationScan('');
          setOverrideLocation('');

          setMandatoryCountingData({
            itemNo: currentLine.itemNo || '',
            itemDescription: currentLine.itemDescription || currentLine.itemNo || '',
            binCode: currentLine.location || '',
            lastCountedDate: checkResult.lastCountedDate,
            daysSinceLastCounted: checkResult.daysSinceLastCounted,
            currentQuantity: checkResult.currentQuantity
          });
          setShowMandatoryCounting(true);
          return; // Block until counting is completed
        }
      } catch (error) {
        console.error('Error checking mandatory counting:', error);
        // Continue with picking if check fails (don't block operations)
      }

      // Normal flow - no mandatory counting required
      setShowLocationScan(false);
      setLocationScan('');
      setOverrideLocation('');

      const quantityToAdd = tempGS1Quantity !== null ? tempGS1Quantity : (currentLine.qty - currentLine.qtyPicked);

      // Show quantity confirmation modal (with package scan check for batch picks)
      showQuantityConfirmWithPackageCheck(currentLine, quantityToAdd);
      setTempGS1Quantity(null);
    } else {
      alert(`Feil lokasjon! Forventet: ${currentLine.location}, Skannet: ${locationScan}`);
    }
  };

  const handleLocationOverride = async () => {
    setShowLocationOverride(false);
    setShowLocationScan(false);

    if (currentLine && overrideLocation) {
      try {
        // Update BinCode in database
        await pickingService.updateBinCode(pListId, currentLine.pListLineNo, overrideLocation);

        // Update the location on the picking line
        const updatedLines = pickingLines.map(l =>
          l.pListLineNo === currentLine.pListLineNo ? { ...l, location: overrideLocation } : l
        );
        setPickingLines(updatedLines);

        // Update currentLine reference with the new location
        const updatedCurrentLine = { ...currentLine, location: overrideLocation };

        const quantityToAdd = tempGS1Quantity !== null ? tempGS1Quantity : (currentLine.qty - currentLine.qtyPicked);

        // Show quantity confirmation modal (with package scan check for batch picks)
        showQuantityConfirmWithPackageCheck(updatedCurrentLine, quantityToAdd);
        setTempGS1Quantity(null);
      } catch (error) {
        console.error('Error updating location override:', error);
        alert('Kunne ikke oppdatere lokasjon. Prøv igjen.');
      }
    }
    setOverrideLocation('');
  };

  // Check if this is the last item from the location (for zero-point counting)
  const checkIfLastItemFromLocation = async (
    itemNo: string,
    binCode: string,
    pickedQuantity: number
  ): Promise<boolean> => {
    if (!itemNo || !binCode) return false;

    try {
      // Get current ItemBins quantity for this item at this location
      const itemBins = await dataInspectionService.getItemBinsByItemNo(itemNo);

      // Find the specific bin
      const binData = itemBins.find(
        (bin) => bin.binCode === binCode
      );

      if (!binData) {
        console.log('⚠️ No ItemBins data found for', itemNo, 'at', binCode);
        return false;
      }

      // Check if availableQuantity equals the picked quantity
      // This means we're picking the last available items from this location
      const availableQty = binData.quantityAvailable || 0;
      const isLastItem = availableQty === pickedQuantity;

      console.log('🔍 Zero-point check:', {
        itemNo,
        binCode,
        availableQty,
        pickedQuantity,
        isLastItem
      });

      return isLastItem;
    } catch (error) {
      console.error('Error checking if last item from location:', error);
      return false;
    }
  };

  // Handle zero-point counting - confirm zero
  const handleZeroPointConfirmZero = async () => {
    if (!zeroPointCountingData || !pickingDetails) return;

    setIsSubmittingZeroPoint(true);
    try {
      // Register count of 0 via mandatoryCountingService (same as mandatory counting)
      const companyNo = pickingDetails.header.companyNo;
      await mandatoryCountingService.submitMandatoryCounting(companyNo, {
        warehouseID: zeroPointCountingData.warehouse,
        itemNo: zeroPointCountingData.itemNo,
        binCode: zeroPointCountingData.binCode,
        countedQuantity: 0, // Zero-point counting confirmed
        pListID: pListId,
        pListLineNo: zeroPointCountingData.pListLineNo
      });

      console.log('✅ Zero-point count registered for', zeroPointCountingData.binCode);

      // Close modal
      setShowZeroPointCounting(false);
      setZeroPointCountingData(null);
    } catch (error) {
      console.error('Error registering zero-point count:', error);
      alert('Kunne ikke registrere nullpunktstelling. Prøv igjen.');
    } finally {
      setIsSubmittingZeroPoint(false);
    }
  };

  // Handle zero-point counting - submit deviation
  const handleZeroPointSubmitDeviation = async (countedQuantity: number) => {
    if (!zeroPointCountingData || !pickingDetails) return;

    setIsSubmittingZeroPoint(true);
    try {
      // Register count with deviation via mandatoryCountingService (same as mandatory counting)
      const companyNo = pickingDetails.header.companyNo;
      await mandatoryCountingService.submitMandatoryCounting(companyNo, {
        warehouseID: zeroPointCountingData.warehouse,
        itemNo: zeroPointCountingData.itemNo,
        binCode: zeroPointCountingData.binCode,
        countedQuantity: countedQuantity, // Actual counted quantity (deviation from expected 0)
        pListID: pListId,
        pListLineNo: zeroPointCountingData.pListLineNo
      });

      console.log('✅ Zero-point deviation registered:', countedQuantity, 'at', zeroPointCountingData.binCode);

      // Close modal
      setShowZeroPointCounting(false);
      setZeroPointCountingData(null);
    } catch (error) {
      console.error('Error registering zero-point deviation:', error);
      alert('Kunne ikke registrere avvik. Prøv igjen.');
    } finally {
      setIsSubmittingZeroPoint(false);
    }
  };

  const handleQuantityConfirm = async (confirmedQuantity: number) => {
    if (!lineToConfirm) return;

    const newTotalQuantity = lineToConfirm.qtyPicked + confirmedQuantity;

    // If we have pending GS1 data with SSCC, create/activate pallet first
    if (pendingGS1Data?.sscc) {
      console.log('📦 Creating/activating pallet from pending GS1 data:', pendingGS1Data.sscc);

      // Check if pallet already exists
      let targetPallet = pallets.find(p => p.sscc === pendingGS1Data.sscc);
      if (!targetPallet) {
        // Create new pallet with GS1 data
        await createNewPallet(pendingGS1Data.sscc, pendingGS1Data);
      } else {
        // Activate existing pallet
        setActivePalletSSCC(pendingGS1Data.sscc);
      }
    }

    // Check if this is the last item from the location
    const shouldCheckZeroPoint = lineToConfirm.itemNo && lineToConfirm.location
      ? await checkIfLastItemFromLocation(
          lineToConfirm.itemNo,
          lineToConfirm.location,
          confirmedQuantity
        )
      : false;

    await handleUpdateQuantity(lineToConfirm.pListLineNo, newTotalQuantity);

    setShowQuantityConfirm(false);
    setLineToConfirm(null);
    setQuantityToConfirm(0);

    // Clear pending GS1 data after processing
    setPendingGS1Data(null);

    // If this was the last item, show zero-point counting modal
    if (shouldCheckZeroPoint && lineToConfirm.warehouseID) {
      setZeroPointCountingData({
        itemNo: lineToConfirm.itemNo!,
        itemDescription: lineToConfirm.itemDescription || '',
        binCode: lineToConfirm.location || '',
        pickedQuantity: confirmedQuantity,
        warehouse: lineToConfirm.warehouseID,
        pListLineNo: lineToConfirm.pListLineNo
      });
      setShowZeroPointCounting(true);
    }
  };

  const handlePickLine = (lineNo: number, qty: number) => {
    const line = pickingLines.find(l => l.pListLineNo === lineNo);
    if (!line) return;

    const quantityToAdd = qty - line.qtyPicked;
    // Show quantity confirmation modal (with package scan check for batch picks)
    showQuantityConfirmWithPackageCheck(line, quantityToAdd);
  };

  const handleUpdateQuantity = async (lineNo: number, newTotalQty: number) => {
    console.log('🔄 Updating quantity for line:', lineNo, 'newTotalQty:', newTotalQty);

    const line = pickingLines.find(l => l.pListLineNo === lineNo);
    if (!line) return;

    // Validate that newTotalQty does not exceed ordered quantity
    if (newTotalQty > line.qty) {
      alert(`Antall kan ikke overstige bestilt antall (${line.qty})`);
      return;
    }

    try {
      // Get StartDateTime for this line from state
      const startDateTime = lineStartTimes.get(lineNo);

      // Update picked quantity in PListLine - send the new total quantity and StartDateTime
      await pickingService.updatePickedQuantity(pListId, lineNo, newTotalQty, undefined, startDateTime);

      // Check if active pallet is empty (frontend-only) - will be converted
      const isConvertingEmptyPallet = activePallet && isEmptyPallet(activePallet);

      if (isConvertingEmptyPallet) {
        console.log('🎉 Converting empty pallet to database pallet:', activePallet.sscc);
      }

      // If there's an active pallet and new quantity > 0, save to OrderSSCC
      if (activePallet && newTotalQty > 0) {
        const line = pickingLines.find(l => l.pListLineNo === lineNo);
        if (line) {
          const quantityForPallet = newTotalQty - line.qtyPicked; // Only the new quantity for this operation
          if (quantityForPallet > 0) {
            // Use batch/expiry data from pendingGS1Data if available
            const batchNo = pendingGS1Data?.batchNo || 'DEFAULT';
            const expiryDate = pendingGS1Data?.expiryDate || null;

            console.log('📋 Creating OrderSSCC with batch/expiry:', { batchNo, expiryDate });

            const ssccRequest: CreateOrderSSCCRequest = {
              companyNo: '', // Will be set by backend
              pListID: pListId,
              orderNo: line.orderNo,
              lineNo: lineNo,
              sscc: activePallet.sscc,
              gtin: line.itemNo,
              quantity: quantityForPallet,
              ssccLevel: 1,
              itemBatchNo: batchNo,
              itemBatchExpDate: expiryDate,
              stackingWeight: 0,
              grossWeight: 0,
              locationName: line.location
            };

            await pickingService.createOrUpdateSSCC(pListId, ssccRequest);
          }
        }
      }

      // Reload pallets from database to get updated data (will convert empty pallet automatically)
      await loadPallets();
      
      // Update local state
      const updatedLines = pickingLines.map(line => {
        if (line.pListLineNo === lineNo) {
          const isFullyPicked = newTotalQty >= line.qty;
          const isPartiallyPicked = newTotalQty > 0 && newTotalQty < line.qty;
          
          return {
            ...line,
            qtyPicked: newTotalQty,
            isCompleted: newTotalQty > 0,
            isPartiallyPicked,
            isFullyPicked,
            palletId: activePallet?.sscc
          };
        }
        return line;
      });
      
      // Find the line that was just updated
      const lineIndex = updatedLines.findIndex(l => l.pListLineNo === lineNo);
      const line = updatedLines[lineIndex];
      
      if (lineIndex !== -1 && line && line.isFullyPicked && lineIndex === activeLineIndex) {
        // Line is fully picked and is the active line
        // Set it as previous and reorganize
        setTimeout(() => {
          setPreviousCompletedLineNo(lineNo);
          const reorganized = reorganizeLines(updatedLines, lineNo);
          setPickingLines(reorganized);

          // Force activeLineIndex to change to trigger useEffect
          // First set to null, then to 0 in next tick
          setActiveLineIndex(null);
          setTimeout(() => setActiveLineIndex(0), 0);
        }, 300);
      } else if (lineIndex !== -1 && line && !line.isFullyPicked) {
        // Partially picked - keep as active, reorganize if needed
        const reorganized = reorganizeLines(updatedLines, previousCompletedLineNo);
        setPickingLines(reorganized);
        setActiveLineIndex(0);
        setCurrentLineIndex(0);
      } else {
        // Just update the lines and reorganize
        const reorganized = reorganizeLines(updatedLines, previousCompletedLineNo);
        setPickingLines(reorganized);
      }
    } catch (error) {
      console.error('Error updating quantity:', error);
      alert('Kunne ikke oppdatere antall. Prøv igjen.');
    }
  };

  const createNewPallet = async (ssccCode?: string, gs1Data?: GS1ScanData) => {
    // SCENARIO 1: Pre-filled pallet (GS1 with item data)
    if (gs1Data && (gs1Data.gtin || gs1Data.itemNo)) {
      const matchedLine = findLineByItem(gs1Data.gtin || gs1Data.itemNo!);

      if (!matchedLine) {
        alert(`Ingen plukklinje matcher GTIN: ${gs1Data.gtin || gs1Data.itemNo}`);
        return;
      }

      // Generate SSCC if not provided
      const finalSSCC = ssccCode || `PALL-${Date.now()}`;

      try {
        // Create OrderSSCC with REAL data from GS1
        const orderSSCCRequest: CreateOrderSSCCRequest = {
          companyNo: pickingDetails!.header.companyNo,
          pListID: pListId,
          orderNo: matchedLine.orderNo,
          lineNo: matchedLine.lineNo,
          sscc: finalSSCC,
          gtin: gs1Data.gtin || gs1Data.itemNo,
          quantity: gs1Data.quantity || matchedLine.qty,
          ssccLevel: 1,
          itemBatchNo: gs1Data.batchNo || 'NO_BATCH',
          itemBatchExpDate: gs1Data.expiryDate || null,
          locationName: matchedLine.binCode
        };

        await pickingService.createOrUpdateSSCC(pListId, orderSSCCRequest);

        // Update picked quantity
        await handleUpdateQuantity(
          matchedLine.pListLineNo,
          matchedLine.qtyPicked + (gs1Data.quantity || matchedLine.qty)
        );

        // Reload from database
        await loadPallets();
        setActivePalletSSCC(finalSSCC);
        alert(`Pall ${finalSSCC} opprettet med vare!`);
        return;
      } catch (error) {
        console.error('Error creating pre-filled pallet:', error);
        alert('Kunne ikke opprette pall. Prøv igjen.');
        return;
      }
    }

    // SCENARIO 2: Empty pallet (no item data)
    // ✅ NEW: Create frontend-only empty pallet (NO DATABASE CALL!)
    createEmptyPallet(ssccCode);
    console.log('✅ Empty pallet created (frontend-only, no database)');
  };

  const setActivePallet = (sscc: string) => {
    setActivePalletSSCC(sscc);
  };

  const deletePallet = async (sscc: string) => {
    try {
      const palletToDelete = pallets.find(p => p.sscc === sscc);
      if (!palletToDelete) return;

      // Check if empty frontend-only pallet
      if (isEmptyPallet(palletToDelete)) {
        console.log('🗑️ Deleting empty frontend-only pallet:', sscc);
        deleteEmptyPallet(sscc);
        console.log('✅ Empty pallet deleted (no database call)');
        return;
      }

      // Database pallet - delete all OrderSSCC records
      const records = await pickingService.getSSCCContents(sscc);

      for (const record of records) {
        await pickingService.deleteSSCC(pListId, record.id);
      }

      // Reload pallets
      await loadPallets();

      console.log('✅ Database pallet deleted:', sscc);
    } catch (error) {
      console.error('Error deleting pallet:', error);
      alert('Kunne ikke slette pall. Prøv igjen.');
    }
  };

  const handleLocationEdit = async (lineNo: number, location: string) => {
    // Guard against duplicate calls - only process if this line is currently being edited
    if (editingLocation !== lineNo) {
      return;
    }

    // Immediately reset editing state to prevent duplicate calls
    setEditingLocation(null);
    setNewLocation('');

    const line = pickingLines.find(l => l.pListLineNo === lineNo);
    if (!line) {
      return;
    }

    // Validate location exists in database (case-insensitive)
    try {
      const locationExists = await stcBinService.exists(
        pickingDetails!.header.companyNo,
        line.warehouseID,
        location.toLowerCase()
      );

      if (!locationExists) {
        alert(`Lokasjon "${location}" finnes ikke i lageret. Vennligst oppgi en gyldig lokasjon.`);
        return;
      }

      // Validate that item has sufficient inventory on the location
      const itemBins = await dataInspectionService.getItemBinsByBinCode(location.toLowerCase());
      const itemOnLocation = itemBins.find(ib => ib.itemNo.toLowerCase() === line.itemNo?.toLowerCase());

      const requiredQuantity = line.qty - line.qtyPicked;
      if (!itemOnLocation || itemOnLocation.quantityAvailable < requiredQuantity) {
        const availableQty = itemOnLocation?.quantityAvailable || 0;
        alert(`Vare "${line.itemNo}" har kun ${availableQty} tilgjengelig på lokasjon "${location}", men ${requiredQuantity} er påkrevd`);
        return;
      }

      // Location and inventory are valid, update in database
      await pickingService.updateBinCode(pListId, lineNo, location);

      // Update local state
      const updatedLines = pickingLines.map(l =>
        l.pListLineNo === lineNo ? { ...l, location } : l
      );
      setPickingLines(updatedLines);
    } catch (error) {
      console.error('Error validating location:', error);
      alert('Kunne ikke validere lokasjon. Prøv igjen.');
    }
  };

  const handleQuantityEdit = async (lineNo: number, newQty: number) => {
    const line = pickingLines.find(l => l.pListLineNo === lineNo);
    if (!line) return;

    // Validate that newQty does not exceed ordered quantity
    if (newQty > line.qty) {
      alert(`Antall kan ikke overstige bestilt antall (${line.qty})`);
      setEditingQuantity(null);
      setNewQuantity('');
      return;
    }

    try {
      // Get StartDateTime for this line from state
      const startDateTime = lineStartTimes.get(lineNo);

      await pickingService.updatePickedQuantity(pListId, lineNo, newQty, undefined, startDateTime);
      
      const updatedLines = pickingLines.map(l => {
        if (l.pListLineNo === lineNo) {
          const isFullyPicked = newQty >= l.qty;
          const isPartiallyPicked = newQty > 0 && newQty < l.qty;
          
          return {
            ...l,
            qtyPicked: newQty,
            isCompleted: newQty > 0,
            isPartiallyPicked,
            isFullyPicked
          };
        }
        return l;
      });

      // If line was just completed, set it as previous
      const editedLine = updatedLines.find(l => l.pListLineNo === lineNo);
      if (editedLine && editedLine.isFullyPicked && !line.isFullyPicked) {
        setPreviousCompletedLineNo(lineNo);
      }
      
      // Reorganize with updated previous
      const newPrevious = editedLine && editedLine.isFullyPicked ? lineNo : previousCompletedLineNo;
      const reorganized = reorganizeLines(updatedLines, newPrevious);
      setPickingLines(reorganized);
      setActiveLineIndex(0);
      
      setEditingQuantity(null);
      setNewQuantity('');

      // Reload pallets from database to get updated data
      await loadPallets();
    } catch (error) {
      console.error('Error updating quantity:', error);
      alert('Kunne ikke oppdatere antall. Prøv igjen.');
    }
  };

  const handleUndoPicking = async (lineNo: number) => {
    // Find the line to get item info for the confirmation message
    const line = pickingLines.find(l => l.pListLineNo === lineNo);
    const lineInfo = line
      ? `${line.itemNo} - ${line.itemDescription || 'Ukjent vare'} (Antall: ${line.qtyPicked})`
      : `Linje ${lineNo}`;

    // Show confirmation dialog
    const confirmed = window.confirm(
      `Er du sikker på at du vil angre plukkingen av denne linjen?\n\n` +
      `${lineInfo}\n\n` +
      `Dette vil:\n` +
      `• Sette plukket antall til 0\n` +
      `• Flytte varene tilbake til original lokasjon\n` +
      `• Slette eventuelle pall-tilordninger\n` +
      `• Slette eventuelle batch-nummer tilordninger\n\n` +
      `Velg "OK" for å angre plukkingen.`
    );

    if (!confirmed) {
      return; // User cancelled
    }

    try {
      // Get StartDateTime for this line from state (even though resetting to 0)
      const startDateTime = lineStartTimes.get(lineNo);

      // Update picked quantity to 0 in PListLine - this triggers backend reversal logic
      await pickingService.updatePickedQuantity(pListId, lineNo, 0, undefined, startDateTime);
      
      // Get and delete all SSCC records for this line
      const allSSCCs = await pickingService.getOrderSSCCs(pListId);
      const lineSSCCs = allSSCCs.filter(sscc => sscc.lineNo === lineNo);
      
      for (const sscc of lineSSCCs) {
        await pickingService.deleteSSCC(pListId, sscc.id);
      }
      
      // Reload pallets from database
      await loadPallets();
      
      // Update local state
      const updatedLines = pickingLines.map(l =>
        l.pListLineNo === lineNo
          ? {
              ...l,
              qtyPicked: 0,
              isCompleted: false,
              isPartiallyPicked: false,
              isFullyPicked: false,
              palletId: undefined
            }
          : l
      );

      // If this was the previous line, clear previous
      if (previousCompletedLineNo === lineNo) {
        setPreviousCompletedLineNo(null);
      }
      
      // Reorganize
      const newPrevious = previousCompletedLineNo === lineNo ? null : previousCompletedLineNo;
      const reorganized = reorganizeLines(updatedLines, newPrevious);
      setPickingLines(reorganized);
      setActiveLineIndex(0);
      
      console.log('Undid picking for line:', lineNo);
    } catch (error) {
      console.error('Error undoing pick:', error);
      alert('Kunne ikke angre plukk. Prøv igjen.');
    }
  };

  const handleCompletePicking = async () => {
    // Check for unused empty pallets
    if (emptyPallets.length > 0) {
      const confirmDelete = window.confirm(
        `Du har ${emptyPallets.length} tomme paller som ikke er brukt.\n\n` +
        `Tomme paller lagres ikke i systemet og vil forsvinne når du fullfører plukking.\n\n` +
        `Vil du slette disse og fortsette?\n\n` +
        `Velg "OK" for å slette tomme paller og fullføre.\n` +
        `Velg "Avbryt" for å gå tilbake.`
      );

      if (!confirmDelete) {
        // User cancelled - go back to picking
        return;
      }

      // User confirmed - delete all empty pallets (they're only in memory)
      // No action needed, they'll be lost when we complete anyway
      console.log(`Brukeren bekreftet sletting av ${emptyPallets.length} tomme paller`);
    }

    // Check for quantity deviations
    const deviationLines = pickingLines
      .filter(line => line.qtyPicked !== line.qty)
      .map(line => ({
        pListLineNo: line.pListLineNo,
        itemNo: line.itemNo || '',
        itemDescription: line.itemDescription || 'Ukjent vare',
        qtyOrdered: line.qty,
        qtyPicked: line.qtyPicked,
        deviation: line.qtyPicked - line.qty
      }));

    // If there are deviations, show deviation modal
    if (deviationLines.length > 0) {
      setQuantityDeviations(deviationLines);
      setShowQuantityDeviation(true);
      return;
    }

    // No deviations - proceed to delivery info modal
    await proceedToDeliveryInfo();
  };

  const handleSendToPacking = async () => {
    // Check for unused empty pallets
    if (emptyPallets.length > 0) {
      const confirmDelete = window.confirm(
        `Du har ${emptyPallets.length} tomme paller som ikke er brukt.\n\n` +
        `Tomme paller lagres ikke i systemet og vil forsvinne når du sender til pakking.\n\n` +
        `Vil du slette disse og fortsette?\n\n` +
        `Velg "OK" for å slette tomme paller og fortsette.\n` +
        `Velg "Avbryt" for å gå tilbake.`
      );

      if (!confirmDelete) {
        return;
      }

      console.log(`Brukeren bekreftet sletting av ${emptyPallets.length} tomme paller`);
    }

    // Check for quantity deviations (but allow them for "Til Pakking")
    const deviationLines = pickingLines
      .filter(line => line.qtyPicked !== line.qty)
      .map(line => ({
        pListLineNo: line.pListLineNo,
        itemNo: line.itemNo || '',
        itemDescription: line.itemDescription || 'Ukjent vare',
        qtyOrdered: line.qty,
        qtyPicked: line.qtyPicked,
        deviation: line.qtyPicked - line.qty
      }));

    // If there are deviations, show confirmation
    if (deviationLines.length > 0) {
      const deviationSummary = deviationLines.map(line =>
        `${line.itemDescription}: ${line.qtyPicked} av ${line.qtyOrdered}`
      ).join('\n');

      const confirmed = window.confirm(
        `Det er avvik på ${deviationLines.length} linje(r):\n\n${deviationSummary}\n\n` +
        `Vil du sende til pakking med disse avvikene?\n\n` +
        `Velg "OK" for å fortsette.\n` +
        `Velg "Avbryt" for å gå tilbake.`
      );

      if (!confirmed) {
        return;
      }
    }

    // Proceed directly to send to packing (no delivery info modal)
    try {
      await pickingService.sendToPacking(pListId);
      alert('Plukklisten er sendt til pakking!');
      onBack(); // Return to picking list
    } catch (error) {
      console.error('Error sending to packing:', error);
      alert('Kunne ikke sende til pakking. Prøv igjen.');
    }
  };

  const proceedToDeliveryInfo = async () => {
    // Reload PListOrders to ensure we have the latest data before opening modal
    try {
      const companyNo = pickingDetails!.header.companyNo;
      console.log('Reloading PListOrders before opening modal...');
      const orders = await plistOrderService.getByPListId(companyNo, pListId);
      console.log('Reloaded PListOrders:', orders);
      setPListOrders(orders);
    } catch (error) {
      console.error('Error reloading PListOrders:', error);
      // Continue anyway - we might not have any orders
    }

    // Open delivery info modal
    setShowDeliveryInfoModal(true);
  };

  const handleQuantityDeviationConfirm = () => {
    // User confirmed to proceed with deviations
    setShowQuantityDeviation(false);
    proceedToDeliveryInfo();
  };

  const handleQuantityDeviationCancel = () => {
    // User cancelled - just close the modal
    setShowQuantityDeviation(false);
  };

  const handleSignatureConfirm = async (signatureDataUrl: string) => {
    try {
      if (!pickingDetails) return;

      // Update PList with signature
      const updatedPList = {
        ID: pickingDetails.header.id,
        CompanyNo: pickingDetails.header.companyNo,
        PListID: pickingDetails.header.pListID,
        Description1: pickingDetails.header.description1,
        Description2: pickingDetails.header.description2,
        PListDate: pickingDetails.header.pListDate,
        NumOfOrderLines: pickingDetails.header.numOfOrderLines,
        Weight: pickingDetails.header.weight,
        Priority: pickingDetails.header.priority,
        PListStatus: pickingDetails.header.pListStatus,
        OperatorID: pickingDetails.header.operatorID,
        LockedBy: pickingDetails.header.lockedBy,
        StartWorkDateTime: pickingDetails.header.startWorkDateTime,
        StopWorkDateTime: pickingDetails.header.stopWorkDateTime,
        DeliveryDate: pickingDetails.header.deliveryDate,
        Signature1: signatureDataUrl
      };

      console.log('Updating PList with signature');
      await pickingService.updatePickingList(pListId, updatedPList);
      console.log('Signature saved successfully');

      // Reload picking details to get updated data
      await loadPickingDetails();

      alert('Signatur lagret!');
    } catch (error) {
      console.error('Error saving signature:', error);
      throw error; // Let the modal handle the error display
    }
  };

  const handleDeliveryInfoConfirm = async (
    pListData: {
      totalVolume: string;
      totalWeight: string;
      numOfPackages: string;
      numOfPallets: string;
      carrierName: string;
      packZoneID: string;
      packageRemark: string;
    },
    pListOrdersData: Map<number, {
      totalVolume: string;
      totalWeight: string;
      numOfPackages: string;
      numOfPallets: string;
      carrierName: string;
      packZoneID: string;
      packageRemark: string;
    }>
  ) => {
    try {
      // Update PList with delivery info
      if (pickingDetails) {
        const updatedPList = {
          ID: pickingDetails.header.id,
          CompanyNo: pickingDetails.header.companyNo,
          PListID: pickingDetails.header.pListID,
          Description1: pickingDetails.header.description1,
          Description2: pickingDetails.header.description2,
          PListDate: pickingDetails.header.pListDate,
          NumOfOrderLines: pickingDetails.header.numOfOrderLines,
          Weight: pickingDetails.header.weight,
          Priority: pickingDetails.header.priority,
          PListStatus: pickingDetails.header.pListStatus,
          OperatorID: pickingDetails.header.operatorID,
          LockedBy: pickingDetails.header.lockedBy,
          StartWorkDateTime: pickingDetails.header.startWorkDateTime,
          StopWorkDateTime: pickingDetails.header.stopWorkDateTime,
          DeliveryDate: pickingDetails.header.deliveryDate,
          TotalVolume: pListData.totalVolume ? parseFloat(pListData.totalVolume) : null,
          TotalWeight: pListData.totalWeight ? parseFloat(pListData.totalWeight) : null,
          NumOfPackages: pListData.numOfPackages ? parseInt(pListData.numOfPackages) : null,
          NumOfPallets: pListData.numOfPallets ? parseFloat(pListData.numOfPallets) : null,
          CarrierName: pListData.carrierName || null,
          PackZoneID: pListData.packZoneID || null,
          PackageRemark: pListData.packageRemark || null
        };

        console.log('Updating PList with data:', updatedPList);
        await pickingService.updatePickingList(pListId, updatedPList);
        console.log('PList updated successfully');
      }

      // Update PListOrders with delivery info
      for (const [orderNo, orderData] of Array.from(pListOrdersData.entries())) {
        const updateRequest = {
          totalVolume: orderData.totalVolume ? parseFloat(orderData.totalVolume) : null,
          totalWeight: orderData.totalWeight ? parseFloat(orderData.totalWeight) : null,
          numOfPackages: orderData.numOfPackages ? parseInt(orderData.numOfPackages) : null,
          numOfPallets: orderData.numOfPallets ? parseFloat(orderData.numOfPallets) : null,
          carrierName: orderData.carrierName || null,
          packZoneID: orderData.packZoneID || null,
          packageRemark: orderData.packageRemark || null
        };

        await plistOrderService.update(
          pickingDetails!.header.companyNo,
          pListId,
          orderNo,
          updateRequest
        );
      }

      // Complete picking after delivery info is saved
      await pickingService.completePicking(pListId);

      // Close modal and go back
      setShowDeliveryInfoModal(false);
      onBack();
    } catch (error) {
      console.error('Error saving delivery info and completing picking:', error);
      throw error; // Let the modal handle the error display
    }
  };

  const handleSplitLine = (lineNo: number) => {
    setSplitLineNo(lineNo);
    const initialSplit: {[palletId: string]: number} = {};
    palletSummaries.forEach(pallet => {
      initialSplit[pallet.sscc] = 0;
    });
    setSplitQuantities(initialSplit);
    setShowSplitModal(true);
  };

  const confirmSplit = async () => {
    if (!splitLineNo) return;

    const line = pickingLines.find(l => l.pListLineNo === splitLineNo);
    if (!line) return;

    const totalSplit = Object.values(splitQuantities).reduce((sum, qty) => sum + qty, 0);
    if (totalSplit > line.qty) {
      alert('Total kan ikke overstige bestilt antall!');
      return;
    }

    try {
      // STEP 1: Delete all existing OrderSSCC records for this line
      const allSSCCs = await pickingService.getOrderSSCCs(pListId);
      const lineSSCCs = allSSCCs.filter(sscc => sscc.lineNo === splitLineNo);

      console.log(`🗑️ Deleting ${lineSSCCs.length} existing OrderSSCC records for line ${splitLineNo}`);
      for (const sscc of lineSSCCs) {
        await pickingService.deleteSSCC(pListId, sscc.id);
      }

      // STEP 2: Create new OrderSSCC records for each pallet allocation
      console.log('📦 Creating new split allocations:', splitQuantities);
      for (const [sscc, qty] of Object.entries(splitQuantities)) {
        if (qty > 0) {
          const ssccRequest: CreateOrderSSCCRequest = {
            companyNo: '', // Will be set by backend
            pListID: pListId,
            orderNo: line.orderNo,
            lineNo: splitLineNo,
            sscc: sscc,
            gtin: line.itemNo,
            quantity: qty,
            ssccLevel: 1,
            itemBatchNo: 'SPLIT_' + Date.now(),
            itemBatchExpDate: null,
            stackingWeight: 0,
            grossWeight: 0,
            locationName: line.location
          };

          await pickingService.createOrUpdateSSCC(pListId, ssccRequest);
        }
      }

      // STEP 3: Update picked quantity in PListLine
      const startDateTime = lineStartTimes.get(splitLineNo);
      await pickingService.updatePickedQuantity(pListId, splitLineNo, totalSplit, undefined, startDateTime);

      // STEP 4: Reload pallets from database
      await loadPallets();
      
      // Update local state
      const updatedLines = pickingLines.map(l =>
        l.pListLineNo === splitLineNo
          ? {
              ...l,
              qtyPicked: totalSplit,
              isCompleted: totalSplit > 0,
              isPartiallyPicked: totalSplit > 0 && totalSplit < l.qty,
              isFullyPicked: totalSplit >= l.qty,
              palletId: Object.keys(splitQuantities).find(sscc => splitQuantities[sscc] > 0)
            }
          : l
      );

      // If line was just completed, set it as previous
      const splitLine = updatedLines.find(l => l.pListLineNo === splitLineNo);
      if (splitLine && splitLine.isFullyPicked) {
        setPreviousCompletedLineNo(splitLineNo);
      }
      
      // Reorganize
      const newPrevious = splitLine && splitLine.isFullyPicked ? splitLineNo : previousCompletedLineNo;
      const reorganized = reorganizeLines(updatedLines, newPrevious);
      setPickingLines(reorganized);
      setActiveLineIndex(0);
      
      setShowSplitModal(false);
      setSplitLineNo(null);
      setSplitQuantities({});
    } catch (error) {
      console.error('Error splitting line:', error);
      alert('Kunne ikke dele linje. Prøv igjen.');
    }
  };

  const confirmQuantityDistribution = async () => {
    if (!distributionLineNo) return;

    const line = pickingLines.find(l => l.pListLineNo === distributionLineNo);
    if (!line) return;

    const totalDistributed = Object.values(distributionQuantities).reduce((sum, qty) => sum + qty, 0);

    if (totalDistributed > line.qty) {
      alert('Total kan ikke overstige bestilt antall!');
      return;
    }

    try {
      // STEP 1: Delete all existing OrderSSCC records for this line
      const allSSCCs = await pickingService.getOrderSSCCs(pListId);
      const lineSSCCs = allSSCCs.filter(sscc => sscc.lineNo === distributionLineNo);

      console.log(`🗑️ Deleting ${lineSSCCs.length} existing OrderSSCC records for line ${distributionLineNo}`);
      for (const sscc of lineSSCCs) {
        await pickingService.deleteSSCC(pListId, sscc.id);
      }

      // STEP 2: Create new OrderSSCC records for each pallet allocation
      console.log('📦 Creating new distribution allocations:', distributionQuantities);
      for (const [sscc, qty] of Object.entries(distributionQuantities)) {
        if (qty > 0) {
          const ssccRequest: CreateOrderSSCCRequest = {
            companyNo: '', // Will be set by backend
            pListID: pListId,
            orderNo: line.orderNo,
            lineNo: distributionLineNo,
            sscc: sscc,
            gtin: line.itemNo,
            quantity: qty,
            ssccLevel: 1,
            itemBatchNo: 'DIST_' + Date.now(),
            itemBatchExpDate: null,
            stackingWeight: 0,
            grossWeight: 0,
            locationName: line.location
          };

          await pickingService.createOrUpdateSSCC(pListId, ssccRequest);
        }
      }

      // STEP 3: Update picked quantity in PListLine
      const startDateTime = lineStartTimes.get(distributionLineNo);
      await pickingService.updatePickedQuantity(pListId, distributionLineNo, totalDistributed, undefined, startDateTime);

      // STEP 4: Reload pallets from database
      await loadPallets();
      
      const isFullyPicked = totalDistributed >= line.qty;
      const isPartiallyPicked = totalDistributed > 0 && totalDistributed < line.qty;

      const updatedLines = pickingLines.map(l =>
        l.pListLineNo === distributionLineNo
          ? {
              ...l,
              qtyPicked: totalDistributed,
              isCompleted: totalDistributed > 0,
              isPartiallyPicked,
              isFullyPicked,
              palletId: Object.keys(distributionQuantities).find(sscc => distributionQuantities[sscc] > 0)
            }
          : l
      );

      // If line was just completed, set it as previous
      if (isFullyPicked) {
        setPreviousCompletedLineNo(distributionLineNo);
      }
      
      // Reorganize
      const newPrevious = isFullyPicked ? distributionLineNo : previousCompletedLineNo;
      const reorganized = reorganizeLines(updatedLines, newPrevious);
      setPickingLines(reorganized);
      setActiveLineIndex(0);
      
      setShowQuantityDistribution(false);
      setDistributionLineNo(null);
      setDistributionQuantities({});
    } catch (error) {
      console.error('Error distributing quantity:', error);
      alert('Kunne ikke distribuere antall. Prøv igjen.');
    }
  };

  // Convert ExtendedPickingLine to PickingLine for components
  const convertToPickingLine = (line: ExtendedPickingLine): PickingLine => {
    // Get split allocations from the database for this line
    const splitAllocations = palletSummaries
      .filter(pallet => pallet.items.some(item => item.lineNo === line.pListLineNo))
      .map(pallet => ({
        palletId: pallet.sscc,
        qty: pallet.items
          .filter(item => item.lineNo === line.pListLineNo)
          .reduce((sum, item) => sum + item.quantity, 0)
      }))
      .filter(allocation => allocation.qty > 0);

    return {
      pListLineNo: line.pListLineNo,
      itemNo: line.itemNo || '',
      itemDescription: line.itemDescription || `${line.itemNo || 'Unknown'} - Beskrivelse`,
      qty: line.qty,
      qtyPicked: line.qtyPicked,
      qtyReserved: line.qtyReserved,
      location: line.location || line.binCode || '',
      orderNo: line.orderNo.toString(),
      orderNoERP: line.orderNoERP,
      customerName: line.customerName,
      isCompleted: line.isCompleted,
      isPartiallyPicked: line.isPartiallyPicked,
      isFullyPicked: line.isFullyPicked,
      image: line.image,
      requireLocationScan: line.requireLocationScan,
      requireItemScan: line.requireItemScan,
      palletId: line.palletId, // Now refers to SSCC
      splitAllocations: splitAllocations.length > 0 ? splitAllocations : undefined,
      // Serial/Batch tracking
      serialNoItemYesNo: line.serialNoItemYesNo,
      batchItemYesNo: line.batchItemYesNo,
      serialNumberCount: line.serialNumbers?.length || 0,
      batchNumberCount: line.batchNumbers?.length || 0
    };
  };

  const convertToPickingLineWithCheck = (line: ExtendedPickingLine | undefined): PickingLine | null => {
    if (!line) return null;
    return convertToPickingLine(line);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 animate-spin border-2 border-blue-500 border-t-transparent rounded-full" />
        <span className="ml-3 text-gray-600">Laster plukk-detaljer...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600 mb-4">{error}</p>
        <button 
          onClick={() => loadPickingDetails()}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
        >
          Prøv igjen
        </button>
      </div>
    );
  }

  if (!pickingDetails) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600">Plukkliste ikke funnet</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile/Enterprise scanner support (Zebra, etc.) - invisible input that captures scanner data */}
      <MobileBarcodeCaptureInput
        enabled={!isAnyModalOpen}
        onScan={(barcode) => {
          console.log('📱 Mobile scanner detected barcode:', barcode);
          processGlobalBarcode(barcode);
        }}
      />

      {/* Header - Mobile optimized with sticky positioning */}
      <div className="bg-white border-b border-gray-200 p-2 sm:p-4 sticky top-0 z-20">
        <div className="flex items-center justify-between max-w-6xl mx-auto">
          <div className="flex items-center gap-2 sm:gap-4 min-w-0">
            <BackButton onClick={onBack} />
            <h1 className="text-base sm:text-xl font-semibold truncate">Plukk #{pickingDetails.header.pListID}</h1>
          </div>

          {/* Action buttons - scrollable on mobile */}
          <div className="flex items-center gap-1 sm:gap-2 overflow-x-auto flex-shrink-0">
            <button
              onClick={() => setShowPrintModal(true)}
              className="flex items-center justify-center min-w-[40px] w-10 h-10 sm:min-w-[44px] sm:w-11 sm:h-11 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              title="Skriv ut (gammel)"
            >
              <Printer size={20} />
            </button>
            <button
              onClick={() => setShowPickingPrintModal(true)}
              className="flex items-center justify-center min-w-[40px] w-10 h-10 sm:min-w-[44px] sm:w-11 sm:h-11 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
              title="Alle etiketter"
            >
              <Tags size={20} />
            </button>
            <button className="hidden sm:flex items-center justify-center min-w-[44px] w-11 h-11 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
              <BarChart3 size={20} />
            </button>
            <button className="hidden sm:flex items-center justify-center min-w-[44px] w-11 h-11 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
              <Info size={20} />
            </button>
            <button
              onClick={() => setShowPalletManager(!showPalletManager)}
              className="relative flex items-center justify-center min-w-[40px] w-10 h-10 sm:min-w-[44px] sm:w-11 sm:h-11 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <Package2 size={20} />
              {activePallet && (
                <div className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 rounded-full"></div>
              )}
            </button>
            <button
              onClick={() => setShowSignatureModal(true)}
              className="flex items-center justify-center min-w-[40px] w-10 h-10 sm:min-w-[44px] sm:w-11 sm:h-11 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              title="Legg til signatur"
            >
              <PenTool size={20} />
            </button>
            <button
              onClick={() => setShowSettingsModal(true)}
              className="flex items-center justify-center min-w-[40px] w-10 h-10 sm:min-w-[44px] sm:w-11 sm:h-11 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              title="Innstillinger"
            >
              <Settings size={20} />
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto p-2 sm:p-4">
        {/* Mobile: Scanner at top, then content. Desktop: Side by side */}
        <div className="flex flex-col lg:grid lg:grid-cols-4 gap-4 sm:gap-6">
          {/* Scanner Column - Full width on mobile, side column on desktop */}
          <div className="order-first lg:order-none">
            <GS1Scanner
              ref={scannerRef}
              onScan={handleScan}
              pallets={palletSummaries}
              activePallet={activePalletSummary}
              onSetActivePallet={setActivePallet}
              onCreatePallet={createNewPallet}
              onCompletePicking={handleCompletePicking}
              onSendToPacking={handleSendToPacking}
            />
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3">
            {/* Active Line */}
            <ActiveLine
              activeLine={activeLine ? convertToPickingLine(activeLine) : null}
              onScanRequired={() => {}}
              onLocationRequired={() => {}}
              editingLocation={editingLocation}
              editingQuantity={editingQuantity}
              newLocation={newLocation}
              newQuantity={newQuantity}
              onStartLocationEdit={(lineNo, location) => {
                setEditingLocation(lineNo);
                setNewLocation(location);
              }}
              onStartQuantityEdit={(lineNo, quantity) => {
                setEditingQuantity(lineNo);
                setNewQuantity(quantity);
              }}
              onLocationChange={setNewLocation}
              onQuantityChange={setNewQuantity}
              onLocationEdit={handleLocationEdit}
              onQuantityEdit={handleQuantityEdit}
              onShowImage={handleShowImage}
            />

            {/* Serial/Batch Number Registration Buttons - show when active line requires it */}
            {activeLine && (activeLine.serialNoItemYesNo || activeLine.batchItemYesNo) && (
              <div className="mb-2 sm:mb-4 p-2 sm:p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                  <div className="text-sm text-amber-800 font-medium">
                    Denne varen krever registrering av{' '}
                    {activeLine.serialNoItemYesNo && activeLine.batchItemYesNo
                      ? 'serienummer og batchnummer'
                      : activeLine.serialNoItemYesNo
                      ? 'serienummer'
                      : 'batchnummer'}
                  </div>
                  <div className="flex gap-2">
                    {activeLine.serialNoItemYesNo && (
                      <button
                        onClick={() => {
                          setSerialNumberLine(activeLine);
                          setShowSerialNumberModal(true);
                        }}
                        className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                      >
                        <span className="hidden sm:inline">Serienummer</span>
                        <span className="sm:hidden">Serie</span>
                        {activeLine.serialNumbers && activeLine.serialNumbers.length > 0 && (
                          <span className="bg-blue-500 px-1.5 py-0.5 rounded text-xs">
                            {activeLine.serialNumbers.length}/{Math.floor(activeLine.qty)}
                          </span>
                        )}
                      </button>
                    )}
                    {activeLine.batchItemYesNo && (
                      <button
                        onClick={() => {
                          setBatchNumberLine(activeLine);
                          setShowBatchNumberModal(true);
                        }}
                        className="flex items-center gap-2 px-3 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors text-sm font-medium"
                      >
                        <span className="hidden sm:inline">Batchnummer</span>
                        <span className="sm:hidden">Batch</span>
                        {activeLine.batchNumbers && activeLine.batchNumbers.length > 0 && (
                          <span className="bg-purple-500 px-1.5 py-0.5 rounded text-xs">
                            {activeLine.batchNumbers.length}
                          </span>
                        )}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}

            <PickingLinesList
              lines={pickingLines.map(convertToPickingLine)}
              currentLineIndex={currentLineIndex}
              activeLineIndex={activeLineIndex}
              previousLineIndex={previousCompletedLineNo !== null ? 1 : null}
              pallets={palletSummaries}
              editingLocation={editingLocation}
              editingQuantity={editingQuantity}
              newLocation={newLocation}
              newQuantity={newQuantity}
              showPalletInfo={showPalletInfo}
              onLineClick={handleLineSelect}
              onLocationEdit={handleLocationEdit}
              onQuantityEdit={handleQuantityEdit}
              onStartLocationEdit={(lineNo, location) => {
                setEditingLocation(lineNo);
                setNewLocation(location);
              }}
              onStartQuantityEdit={(lineNo, quantity) => {
                setEditingQuantity(lineNo);
                setNewQuantity(quantity);
              }}
              onLocationChange={setNewLocation}
              onQuantityChange={setNewQuantity}
              onSplitLine={handleSplitLine}
              onPickLine={handlePickLine}
              onUndoPicking={handleUndoPicking}
              onShowPalletInfo={setShowPalletInfo}
              onReorder={handleReorder}
              enableDragAndDrop={enableDragAndDrop}
            />
          </div>
        </div>
      </div>

      {/* Image Panel */}
      <ImagePanel
        isOpen={showImagePanel && !!activeLine?.image}
        imageUrl={activeLine?.image || ''}
        itemDescription={activeLine?.itemDescription || ''}
        onZoom={() => {
          if (activeLine?.image) {
            setSelectedImage(activeLine.image);
            setShowImageModal(true);
          }
        }}
        onClose={() => setShowImagePanel(false)}
      />

      {/* Modals */}
      <LocationScanModal
        isOpen={showLocationScan}
        expectedLocation={currentLine?.location || ''}
        tempGS1Quantity={tempGS1Quantity}
        locationScan={locationScan}
        onLocationScanChange={setLocationScan}
        onConfirm={handleLocationScanConfirm}
        onOverride={() => setShowLocationOverride(true)}
        onClose={() => {
          setShowLocationScan(false);
          setLocationScan('');
          setTempGS1Quantity(null);
        }}
      />

      <LocationOverrideModal
        isOpen={showLocationOverride}
        expectedLocation={currentLine?.location || ''}
        overrideLocation={overrideLocation}
        companyNo={pickingDetails.header.companyNo}
        warehouseID={currentLine?.warehouseID || ''}
        itemNo={currentLine?.itemNo || ''}
        requiredQuantity={currentLine ? currentLine.qty - currentLine.qtyPicked : 0}
        onLocationChange={setOverrideLocation}
        onConfirm={handleLocationOverride}
        onClose={() => {
          setShowLocationOverride(false);
          setOverrideLocation('');
        }}
      />

      <ImageModal
        isOpen={showImageModal}
        imageUrl={selectedImage}
        onClose={() => setShowImageModal(false)}
      />

      <SplitModal
        isOpen={showSplitModal}
        line={convertToPickingLineWithCheck(splitLineNo ? pickingLines.find(l => l.pListLineNo === splitLineNo) : undefined)}
        pallets={palletSummaries}
        splitQuantities={splitQuantities}
        onQuantityChange={(palletId, quantity) => setSplitQuantities({
          ...splitQuantities,
          [palletId]: quantity
        })}
        onConfirm={confirmSplit}
        onClose={() => {
          setShowSplitModal(false);
          setSplitLineNo(null);
          setSplitQuantities({});
        }}
      />

      <QuantityDistributionModal
        isOpen={showQuantityDistribution}
        line={convertToPickingLineWithCheck(distributionLineNo ? pickingLines.find(l => l.pListLineNo === distributionLineNo) : undefined)}
        pallets={palletSummaries}
        distributionQuantities={distributionQuantities}
        onQuantityChange={(palletId, quantity) => setDistributionQuantities({
          ...distributionQuantities,
          [palletId]: quantity
        })}
        onConfirm={confirmQuantityDistribution}
        onClose={() => {
          setShowQuantityDistribution(false);
          setDistributionLineNo(null);
          setDistributionQuantities({});
        }}
      />

      <PalletInfoModal
        isOpen={!!showPalletInfo}
        pallet={showPalletInfo ? palletSummaries.find(p => p.sscc === showPalletInfo) || null : null}
        onClose={() => setShowPalletInfo(null)}
      />

      <DeliveryInfoModal
        isOpen={showDeliveryInfoModal}
        pListId={pListId}
        companyNo={pickingDetails?.header.companyNo || ''}
        pListOrders={pListOrders}
        userProfile={userProfile}
        onConfirm={handleDeliveryInfoConfirm}
        onClose={() => setShowDeliveryInfoModal(false)}
      />

      {/* Package scan modal for batch picking - must scan correct package before quantity entry */}
      <PackageScanModal
        isOpen={showPackageScan}
        onClose={() => {
          setShowPackageScan(false);
          setLineToConfirm(null);
          setQuantityToConfirm(0);
        }}
        onConfirm={handlePackageScanConfirm}
        packageNo={lineToConfirm?.packageNo ?? 0}
        visiblePackageNo={lineToConfirm?.visiblePackageNo}
        itemNo={lineToConfirm?.itemNo}
        customerName={lineToConfirm?.customerName}
      />

      <QuantityConfirmModal
        isOpen={showQuantityConfirm}
        line={lineToConfirm}
        proposedQuantity={quantityToConfirm}
        onConfirm={handleQuantityConfirm}
        onClose={() => {
          setShowQuantityConfirm(false);
          setLineToConfirm(null);
          setQuantityToConfirm(0);
        }}
      />

      <QuantityDeviationModal
        isOpen={showQuantityDeviation}
        deviations={quantityDeviations}
        onConfirm={handleQuantityDeviationConfirm}
        onCancel={handleQuantityDeviationCancel}
      />

      <SignatureModal
        isOpen={showSignatureModal}
        onConfirm={handleSignatureConfirm}
        onClose={() => setShowSignatureModal(false)}
      />

      <MandatoryCountingModal
        isOpen={showMandatoryCounting}
        itemNo={mandatoryCountingData?.itemNo || ''}
        itemDescription={mandatoryCountingData?.itemDescription || ''}
        binCode={mandatoryCountingData?.binCode || ''}
        lastCountedDate={mandatoryCountingData?.lastCountedDate}
        daysSinceLastCounted={mandatoryCountingData?.daysSinceLastCounted}
        currentQuantity={mandatoryCountingData?.currentQuantity || 0}
        onSubmit={handleMandatoryCountingSubmit}
        isSubmitting={isSubmittingCounting}
      />

      <ZeroPointCountingModal
        isOpen={showZeroPointCounting}
        itemNo={zeroPointCountingData?.itemNo || ''}
        itemDescription={zeroPointCountingData?.itemDescription || ''}
        binCode={zeroPointCountingData?.binCode || ''}
        pickedQuantity={zeroPointCountingData?.pickedQuantity || 0}
        onConfirmZero={handleZeroPointConfirmZero}
        onSubmitDeviation={handleZeroPointSubmitDeviation}
        isSubmitting={isSubmittingZeroPoint}
      />

      <SettingsModal
        isOpen={showSettingsModal}
        autoShowImage={autoShowImage}
        onAutoShowImageChange={setAutoShowImage}
        enableDragAndDrop={enableDragAndDrop}
        onEnableDragAndDropChange={setEnableDragAndDrop}
        enableRapidScanning={enableRapidScanning}
        onEnableRapidScanningChange={setEnableRapidScanning}
        onClose={() => setShowSettingsModal(false)}
      />

      <PrintModal
        isOpen={showPrintModal}
        onClose={() => setShowPrintModal(false)}
        companyNo={pickingDetails?.header.companyNo || ''}
        documentTitle={pickingDetails ? `Plukkliste #${pickingDetails.header.pListID}` : 'Plukkliste'}
        pListId={pListId}
        pickingDetails={pickingDetails}
      />

      {/* New Picking Print Modal with all label types */}
      <PickingPrintModal
        isOpen={showPickingPrintModal}
        onClose={() => setShowPickingPrintModal(false)}
        pickingDetails={pickingDetails}
        activeLine={activeLine}
      />

      {/* Serial Number Registration Modal */}
      <SerialNumberModal
        isOpen={showSerialNumberModal}
        line={serialNumberLine}
        pListId={pListId}
        onClose={() => {
          setShowSerialNumberModal(false);
          setSerialNumberLine(null);
        }}
        onSerialNumbersChanged={() => {
          // Refresh picking details to update serial numbers
          loadPickingDetails();
        }}
      />

      {/* Batch Number Registration Modal */}
      <BatchNumberModal
        isOpen={showBatchNumberModal}
        line={batchNumberLine}
        pListId={pListId}
        onClose={() => {
          setShowBatchNumberModal(false);
          setBatchNumberLine(null);
        }}
        onBatchNumbersChanged={() => {
          // Refresh picking details to update batch numbers
          loadPickingDetails();
        }}
      />

      {/* Pallet Manager */}
      {showPalletManager && (
        <PalletManager
          pallets={palletSummaries}
          activePalletSSCC={activePalletSSCC}
          onCreatePallet={createNewPallet}
          onSetActivePallet={setActivePallet}
          onDeletePallet={deletePallet}
          onClose={() => setShowPalletManager(false)}
        />
      )}

      {/* Bottom Status Bar */}
      <StatusBar
        completedLines={completedLines}
        totalLines={pickingLines.length}
        pallets={palletSummaries}
        activePalletSSCC={activePalletSSCC}
        priority={pickingDetails.header.priority}
      />
    </div>
  );
};